import { DropDown, DropDownListItem } from 'components/DropDown';
import { Prototype } from 'components/Prototype';
import useDeleteAssignment from 'hooks/useDeleteAssignment';
import { useCopyContent } from 'hooks/useCopyContent';
import React from 'react';
import { useTranslation } from 'react-i18next';
import OutsideClickHandler from 'react-outside-click-handler';
import { useSelector } from 'react-redux';
import { getEditSetUrl, getPreviewSetUrl } from 'utils/pathUtils';
import { CONTENT_CREATOR_ROLES } from 'utils/constants';
import { AnalyticsService } from 'utils/AnalyticsService';

interface ICourseAssignmentsTableMenuProps {
  handleOutsideClick: () => void;
  course: any;
  isShared: boolean;
  onShare: () => void;
  left: number;
  top: number;
  currentUser: any;
  selectedSet: any;
}

const CourseAssignmentsTableMenu: React.FC<ICourseAssignmentsTableMenuProps> = (props) => {
  const { t } = useTranslation();
  const userRole = props.currentUser.primaryPartner.meta.role;
  function getInstructors() {
    return props.course.instructors;
  }
  const instructors = useSelector(getInstructors) || {};
  const userIsCourseInstructor = instructors.map((instructor) => instructor.id).includes(props.currentUser.id);
  const isScorm: boolean = props.selectedSet.goalType === 'scorm';
  const userCanEditContent = props.course?.meta?.canEdit ?? false;

  const [deleteAssignment] = useDeleteAssignment();
  const { canCopy, canCopyAsAssessment, canCopyAsLearningSet, handleCopy } = useCopyContent({
    courseId: props.course.id,
    canEditContent: userCanEditContent,
    onCopyStart: props.handleOutsideClick,
  });

  const onShareClick = (): void => {
    if (props.isShared) {
      AnalyticsService.getInstance().track('button_clicked', { button_type: 'Share assignment' });
    } else {
      AnalyticsService.getInstance().track('button_clicked', { button_type: 'Unshare assignment' });
    }
    props.handleOutsideClick();
    props.onShare();
  };

  const editContent = (_event: any): void => {
    AnalyticsService.getInstance().track('button_clicked', { button_type: 'Edit assignment' });
    window.location.href = getEditSetUrl(props.selectedSet.learnVersion, props.selectedSet.id, props.course.id);
  };

  const previewContent = (_event: any): void => {
    AnalyticsService.getInstance().track('button_clicked', { button_type: 'Preview assignment' });
    props.handleOutsideClick();
    const { selectedSet: set } = props;
    window.location.href = getPreviewSetUrl(set.learnVersion, set.id);
  };

  const onRemoveClick = () => {
    AnalyticsService.getInstance().track('button_clicked', { button_type: 'Remove assignment from course' });
    props.handleOutsideClick(); // Close the actions menu
    deleteAssignment({ courseId: props.course.id, setId: props.selectedSet.id });
  };

  const onExportClick = async () => {
    props.handleOutsideClick(); // Close the actions menu

    window.open(`/api/v3/sets/${props.selectedSet.id}/export`);
  };

  const canPreview = (): boolean => {
    return !isScorm && (props.currentUser.primaryPartner.meta.canReadContent || userIsCourseInstructor);
  };

  const canShare = (): boolean => {
    return CONTENT_CREATOR_ROLES.includes(userRole);
  };

  const canDelete = (): boolean => {
    return userCanEditContent && CONTENT_CREATOR_ROLES.includes(userRole);
  };

  const canEdit = (): boolean => {
    if (isScorm) {
      return false;
    } else {
      return props.selectedSet.meta.canEdit;
    }
  };

  const canExport = () => {
    const isAllowedAssignmentType = ['set', 'assessment'].includes(props.selectedSet.goalType);
    const exportEnabled = props.course.partner.partnerSettings.allowContentImportExport;
    return canEdit() && isAllowedAssignmentType && exportEnabled;
  };

  return (
    <OutsideClickHandler onOutsideClick={props.handleOutsideClick}>
      <DropDown top={props.top} left={props.left - 3} style={{ zIndex: 11 }} unwrapped={true}>
        {canEdit() && (
          <DropDownListItem>
            <button onClick={editContent}>{t('Edit')}</button>
          </DropDownListItem>
        )}
        {canPreview() && (
          <DropDownListItem>
            <button onClick={previewContent}>{t('Preview')}</button>
          </DropDownListItem>
        )}
        {canCopy(props.selectedSet) && (
          <DropDownListItem>
            <button onClick={() => handleCopy(props.selectedSet, props.selectedSet.goalType, 'Copy assignment')}>
              {t('Copy')}
            </button>
          </DropDownListItem>
        )}
        {canCopyAsAssessment(props.selectedSet) && (
          <DropDownListItem>
            <button onClick={() => handleCopy(props.selectedSet, 'assessment', 'Copy as assessment')}>
              {t('Copy as an assessment')}
            </button>
          </DropDownListItem>
        )}
        {canCopyAsLearningSet(props.selectedSet) && (
          <DropDownListItem>
            <button onClick={() => handleCopy(props.selectedSet, 'set', 'Copy as learning set')}>
              {t('Copy as a learning set')}
            </button>
          </DropDownListItem>
        )}
        {canDelete() && (
          <DropDownListItem>
            <button onClick={onRemoveClick}>{t('Remove from course')}</button>
          </DropDownListItem>
        )}
        {canShare() && (
          <DropDownListItem>
            <button onClick={onShareClick}>
              {props.isShared ? t('Remove from shared assignments') : t('Include in shared assignments')}
            </button>
          </DropDownListItem>
        )}
        {canExport() && (
          <DropDownListItem>
            <button onClick={onExportClick}>{t('Export')}</button>
          </DropDownListItem>
        )}
      </DropDown>
    </OutsideClickHandler>
  );
};

export default CourseAssignmentsTableMenu;
